import { appApi } from '../app_api';

export const matchesApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getMatches: builder.query({
      query: ({ groupId, showPlayedMatches, playerName }) => {
        const params = new URLSearchParams();
        if (typeof showPlayedMatches !== 'undefined') {
          params.set('showPlayedMatches', String(showPlayedMatches));
        }
        if (typeof playerName !== 'undefined' && playerName !== null && playerName !== '') {
          params.set('playerName', playerName);
        }
        const queryString = params.toString();
        return `/groups/${groupId}/matches${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response) => Array.isArray(response.data) ? response.data : [],
      providesTags: (result, error, { groupId }) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Match', id })),
              { type: 'GroupMatches', id: groupId },
            ]
          : [{ type: 'GroupMatches', id: groupId }],
    }),
    enterScore: builder.mutation({
      query: ({ matchId, score1, score2, group }) => {
        return {
          url: `/matches/${matchId}`,
          method: 'PUT',
          body: { score1, score2 },
        };
      },
      invalidatesTags: (result, error, { group, matchId }) =>
        result && result.lastMatch
          ? [
              { type: 'Match', id: matchId },
              { type: 'GroupLeaderboard', id: group.id },
              { type: 'Group', id: group.id },
              { type: 'GroupConflicts', id: group.id },
              { type: 'EventFinishedAllMatchesRound', id: group.roundId },
              { type: 'EventGroupsReviewedRound', id: group.roundId },
              { type: 'GroupInstances', id: group.id },
              { type: 'RoundCanContinue', id: group.roundId },
              { type: 'RoundQualifiedPlayersCount', id: group.roundId },
            ]
          : [
              { type: 'Match', id: matchId },
              { type: 'GroupLeaderboard', id: group.id },
              { type: 'GroupInstances', id: group.id },
            ]
          }),
    enterBracketScore: builder.mutation({
      query: ({ matchId, score1, score2, bracket }) => {
        return {
          url: `/matches/${matchId}`,
          method: 'PUT',
          body: { score1, score2 },
        };
      },
      invalidatesTags: (result, error, { bracket, matchId }) => [
        { type: 'Match', id: matchId },
        { type: 'RoundBrackets', id: null },
        { type: 'Bracket', id: bracket.id },
        { type: 'Bracket', id: bracket.nextBracketId},
        { type: 'Round', id: bracket.roundId }
      ],
    }),
    enterRandomScores: builder.mutation({
      query: ({ group }) => {
        return {
          url: `/groups/${group.id}/matches/update_all_randomly`,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, { group }) => [
        { type: 'GroupMatches', id: group.id },
        { type: 'GroupLeaderboard', id: group.id },
        { type: 'Group', id: group.id },
        { type: 'GroupConflicts', id: group.id },
        { type: 'EventFinishedAllMatchesRound', id: group.roundId },
        { type: 'EventGroupsReviewedRound', id: group.roundId },
        { type: 'GroupInstances', id: group.id },
        { type: 'RoundCanContinue', id: group.roundId },
        { type: 'RoundQualifiedPlayersCount', id: group.roundId },
      ],
    }),
    assignBracketMatchTable: builder.mutation({
      query: ({ matchId, tableId }) => ({
        url: `/matches/${matchId}`,
        method: 'PUT',
        body: { table_id: tableId },
      }),
      invalidatesTags: (result, error, { bracketId, roundId }) => [
        { type: 'Bracket', id: bracketId },
        { type: 'RoundBrackets', id: roundId },
      ],
    }),
    assignMatchTable: builder.mutation({
      query: ({ matchId, tableId }) => ({
        url: `/matches/${matchId}`,
        method: 'PUT',
        body: { table_id: tableId },
      }),
      invalidatesTags: (result, error, { matchId, groupId }) => [
        { type: 'Match', id: matchId },
        { type: 'GroupMatches', id: groupId },
      ],
    }),
    // Hand-sets a single match's start time (flags it manually_scheduled
    // server-side, so an auto-schedule re-run keeps the override).
    setMatchTime: builder.mutation({
      query: ({ matchId, startAt }) => ({
        url: `/matches/${matchId}`,
        method: 'PUT',
        body: { start_at: startAt },
      }),
      invalidatesTags: (result, error, { matchId, groupId }) => [
        { type: 'Match', id: matchId },
        { type: 'GroupMatches', id: groupId },
      ],
    }),
    // Bracket variant of setMatchTime — same PUT, but invalidates the bracket
    // caches (RoundBrackets/Bracket) instead of GroupMatches.
    setBracketMatchTime: builder.mutation({
      query: ({ matchId, startAt }) => ({
        url: `/matches/${matchId}`,
        method: 'PUT',
        body: { start_at: startAt },
      }),
      invalidatesTags: (result, error, { bracketId, roundId }) => [
        { type: 'Bracket', id: bracketId },
        { type: 'RoundBrackets', id: roundId },
      ],
    }),
  }),
});

export const {
  useGetMatchesQuery,
  useGetMatchesTableViewQuery,
  useEnterScoreMutation,
  useEnterRandomScoresMutation,
  useEnterBracketScoreMutation,
  useAssignMatchTableMutation,
  useAssignBracketMatchTableMutation,
  useSetMatchTimeMutation,
  useSetBracketMatchTimeMutation,
} = matchesApi;